import { useQuery } from "@tanstack/react-query";
import { LoadErrorState } from "../ui/LoadState";
import { api } from "../../lib/api";
import type { KnowledgeBaseEntry } from "../../../../shared/crawl";

type IndexedSourceRow = {
  source_url: string;
  chunk_count: number;
  indexed_at: string;
};

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export function IndexedSourcesPanel({
  brandId,
  shopifyProductId,
}: {
  brandId: string;
  shopifyProductId?: string | null;
}) {
  const query = useQuery({
    queryKey: ["brands", brandId, "knowledge", shopifyProductId ?? null],
    queryFn: () => api.get<{ data: IndexedSourceRow[] | KnowledgeBaseEntry[] }>(
      shopifyProductId
        ? `/api/brands/${brandId}/knowledge?platform=shopify&shopify_product_id=${encodeURIComponent(shopifyProductId)}&limit=20`
        : `/api/brands/${brandId}/knowledge?limit=200&distinct=source_url`,
    ),
    enabled: Boolean(brandId),
  });
  const rows = (query.data?.data ?? []) as Array<IndexedSourceRow | KnowledgeBaseEntry>;
  const sourceRows = rows as IndexedSourceRow[];
  const productRows = rows as KnowledgeBaseEntry[];

  return (
    <section className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
      <div className="font-data text-[11px] uppercase tracking-wider text-slate-500">
        {shopifyProductId ? "Shopify knowledge" : "Indexed sources"}
      </div>
      <h3 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">
        {shopifyProductId ? "Shopify product knowledge" : "Crawled website and social content"}
      </h3>
      {shopifyProductId ? (
        <p className="mt-2 text-sm text-slate-600">
          Filtered to product <span className="font-medium text-slate-900">{shopifyProductId}</span>.
        </p>
      ) : null}

      {query.isLoading ? (
        <div className="mt-4 text-sm text-slate-500">Loading indexed sources…</div>
      ) : query.isError && !query.data ? (
        <div className="mt-4"><LoadErrorState compact title="Indexed sources could not be loaded" description="This does not mean nothing is indexed." onRetry={() => void query.refetch()} /></div>
      ) : rows.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600">
          {shopifyProductId ? "No Shopify product knowledge indexed for this product yet." : "No indexed sources yet."}
        </div>
      ) : shopifyProductId ? (
        <div className="mt-4 space-y-3">
          {productRows.map((row) => (
            <article key={row.id} className="rounded-xl border border-slate-200 p-3">
              <div className="text-sm font-medium text-slate-900">{row.title || row.source_url}</div>
              <div className="mt-1 text-xs text-slate-500">
                {row.source_type} · Indexed {formatDate(row.indexed_at)}
              </div>
              <div className="mt-3 text-sm text-slate-700">
                {row.content}
              </div>
              <div className="mt-3 break-all text-xs text-slate-500">{row.source_url}</div>
            </article>
          ))}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {sourceRows.map((row) => (
            <article key={row.source_url} className="rounded-xl border border-slate-200 p-3">
              <div className="break-all text-sm font-medium text-slate-900">{row.source_url}</div>
              <div className="mt-1 text-xs text-slate-500">
                {row.chunk_count} chunks · Last indexed {formatDate(row.indexed_at)}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
